import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  Launch as LaunchIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useStringsContext } from '../../../../contexts/strings';
import {
  SchedulePushdNotificationBody,
  PushNotificationType,
} from '../../../../types/notification';

interface NotificationStats {
  failedNotifications: number;
  successNotifications: number;
  successRate: number;
  message: string;
}

interface ExtendedNotificationData extends SchedulePushdNotificationBody {
  result?: NotificationStats;
  sentAt?: string;
}

interface NotificationStatsDialogProps {
  open: boolean;
  onClose: () => void;
  data: ExtendedNotificationData | null;
}

export function NotificationStatsDialog({ open, onClose, data }: NotificationStatsDialogProps) {
  const { strings } = useStringsContext();
  const t = strings.pushNotifications;

  if (!data) return null;

  const { notification, app, scheduledTime, createdBy, createdAt, sentAt, status, result } = data;

  const failedNotifications = result?.failedNotifications ?? 0;
  const successNotifications = result?.successNotifications ?? 0;
  const successRate = result?.successRate ?? 0;

  const total = successNotifications + failedNotifications;
  const successPercentage = (successRate * 100).toFixed(1);
  const isSuccess = successRate >= 0.95;

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      SENT: t.statusSent || 'Sent',
      SCHEDULED: t.statusScheduled || 'Scheduled',
      FAILED: t.statusNotSent || 'Failed',
    };
    return statusMap[status] || status;
  };

  const getTypeLabel = (type: PushNotificationType) => {
    const typeMap: Record<PushNotificationType, string> = {
      ARTICLES: t.ARTICLES || 'Articles',
      POLL: t.POLL || 'Polls',
      STORIES: t.STORIES || 'Stories',
      MATCH: t.MATCH || 'Matches',
      NEWS: t.NEWS || 'News',
      WEB_PAGE: t.WEB_PAGE || 'Web Page',
      SCENE: 'Scene',
      FOTBOLLPLAY: 'Fotbollplay',
    };
    return typeMap[type] || type;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box>
          <DialogTitle sx={{ p: 0, mb: 0.5, fontSize: '1.25rem', fontWeight: 600 }}>
            {t.fieldStats || 'Stats'}
          </DialogTitle>
          <Typography variant="body2" color="text.secondary">
            {notification.title}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Stats Cards */}
        <Box sx={{ p: 3, bgcolor: 'background.default' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'background.paper',
                }}
                elevation={0}
                variant="outlined"
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isSuccess ? 'success.light' : 'warning.light',
                    color: isSuccess ? 'success.main' : 'warning.main',
                  }}
                >
                  <TrendingUpIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t.successRate || 'Success Rate'}
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {successPercentage}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'background.paper',
                }}
                elevation={0}
                variant="outlined"
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'success.light',
                    color: 'success.main',
                  }}
                >
                  <CheckCircleIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t.successful || 'Successful'}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    {successNotifications.toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  bgcolor: 'background.paper',
                }}
                elevation={0}
                variant="outlined"
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'error.light',
                    color: 'error.main',
                  }}
                >
                  <ErrorIcon />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t.failed || 'Failed'}
                  </Typography>
                  <Typography variant="h6" fontWeight={600} color="error.main">
                    {failedNotifications.toLocaleString()}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t.deliveryProgress || 'Delivery Progress'}
              </Typography>
              <Typography variant="caption" fontWeight={500}>
                {successNotifications.toLocaleString()} / {total.toLocaleString()}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={successRate * 100}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                  bgcolor: isSuccess ? 'success.main' : 'warning.main',
                },
              }}
            />
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            {t.notificationDetails || 'Notification Details'}
          </Typography>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LabelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.fieldNameApp || 'App'}:
                </Typography>
                <Chip label={app} size="small" variant="outlined" />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LabelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.status || 'Status'}:
                </Typography>
                <Chip
                  label={getStatusLabel(status)}
                  size="small"
                  color={
                    status === 'SENT' ? 'success' : status === 'SCHEDULED' ? 'info' : 'default'
                  }
                />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.fieldNameDate || 'Date'}:
                </Typography>
                <Typography variant="body2">
                  {scheduledTime ? moment(scheduledTime).format('YYYY-MM-DD HH:mm') : 'N/A'}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.sentAt || 'Sent'}:
                </Typography>
                <Typography variant="body2">
                  {sentAt ? <>{moment(sentAt).format('YYYY-MM-DD HH:mm')}</> : 'N/A'}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.fieldNameAuthor || 'Author'}:
                </Typography>
                <Typography variant="body2">{createdBy || 'Unknown'}</Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.createdAt || 'Created'}:
                </Typography>
                <Typography variant="body2">
                  {moment(createdAt).format('YYYY-MM-DD HH:mm')}
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LabelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.fieldNameType || 'Type'}:
                </Typography>
                <Chip label={getTypeLabel(notification.type)} size="small" variant="outlined" />
              </Stack>
            </Grid>

            <Grid item xs={12} sm={6} sx={{ display: 'none' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LabelIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  {t.fieldNameId || 'ID'}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                  {data.id}
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {t.fieldNameTarget || 'Target Segment'}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {notification.target.value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t.selectSegment || 'Type'}: {notification.target.type}
            </Typography>
          </Box>

          {notification.webPageUrl && (
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LaunchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                  URL:
                </Typography>
                <Typography
                  variant="body2"
                  component="a"
                  href={notification.webPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {notification.webPageUrl}
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
